import Box from "@cloudscape-design/components/box"
import Container from "@cloudscape-design/components/container"
import ExpandableSection from "@cloudscape-design/components/expandable-section"
import Header from "@cloudscape-design/components/header"
import Icon, { type IconProps } from "@cloudscape-design/components/icon"
import Link from "@cloudscape-design/components/link"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import StatusIndicator, {
  type StatusIndicatorProps,
} from "@cloudscape-design/components/status-indicator"
import Steps, { type StepsProps } from "@cloudscape-design/components/steps"
import { useMemo, useState } from "react"
import { RunStatus } from "@/generated/sdk"
import { useDataStreamsList, useStudyRunsList } from "../../../hooks/queries/useIngestion"
import { useIngestionSpecsList } from "../../../hooks/queries/useIngestionSpecs"
import { DataStream, IngestionRun } from "../../../services/ingestionApi"
import QuarantineRecordsTable from "./QuarantineRecordsTable"

interface DataStreamHistoryProps {
  studyId: string
}

interface StreamWithRuns {
  stream: DataStream
  domain: string
  runs: IngestionRun[]
}

const STATUS_INDICATOR: Record<string, StatusIndicatorProps.Type> = {
  [RunStatus.Completed]: "success",
  [RunStatus.Failed]: "error",
  [RunStatus.PartiallyCompleted]: "warning",
  [RunStatus.Running]: "in-progress",
}

const STEP_ICON: Record<string, { name: IconProps.Name; variant: IconProps.Variant }> = {
  success: { name: "status-positive", variant: "success" },
  error: { name: "status-negative", variant: "error" },
  warning: { name: "status-warning", variant: "warning" },
  "in-progress": { name: "status-in-progress", variant: "link" },
  pending: { name: "status-pending", variant: "subtle" },
}

const DataStreamHistory = ({ studyId }: DataStreamHistoryProps) => {
  // Each stream owns its own open/closed state. All start collapsed; toggling one never affects the
  // others (no single-active-index accordion behavior).
  const [expandedStreams, setExpandedStreams] = useState<Set<string>>(new Set())
  const [selectedRun, setSelectedRun] = useState<{
    studyId: string
    runId: string
    specId: string
  } | null>(null)

  const toggleStream = (dataStreamId: string) => {
    setExpandedStreams((prev) => {
      const next = new Set(prev)
      if (next.has(dataStreamId)) {
        next.delete(dataStreamId)
      } else {
        next.add(dataStreamId)
      }
      return next
    })
  }

  const streamsQuery = useDataStreamsList(studyId)
  const specsQuery = useIngestionSpecsList(studyId)
  const runsQuery = useStudyRunsList(studyId)
  const loading = streamsQuery.isLoading || specsQuery.isLoading || runsQuery.isLoading

  // Join data streams to their runs and spec domains client-side. Only streams with at least one
  // run are shown, most-recently-active first.
  const streams: StreamWithRuns[] = useMemo(() => {
    const specDomains: Record<string, string> = {}
    specsQuery.data?.ingestionSpecs?.forEach((spec) => {
      specDomains[spec.specId] = spec.domain || "Unknown Domain"
    })

    const runsByStream: Record<string, IngestionRun[]> = {}
    runsQuery.data?.runs?.forEach((run) => {
      if (run.dataStreamId) {
        ;(runsByStream[run.dataStreamId] ??= []).push(run)
      }
    })

    return (streamsQuery.data?.dataStreams || [])
      .filter((s) => runsByStream[s.dataStreamId]?.length > 0)
      .map((stream) => ({
        stream,
        domain: specDomains[stream.specId] || "Unknown",
        runs: (runsByStream[stream.dataStreamId] || []).sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        ),
      }))
      .sort((a, b) => {
        const aLatest = a.runs[0]?.createdAt || ""
        const bLatest = b.runs[0]?.createdAt || ""
        return new Date(bLatest).getTime() - new Date(aLatest).getTime()
      })
  }, [streamsQuery.data, specsQuery.data, runsQuery.data])

  const formatDate = (date: string | Date) => {
    const d = new Date(date)
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const header = <Header variant="h2">Recent Uploads</Header>

  if (loading) {
    return (
      <Container header={header}>
        <Box textAlign="center" padding="l" color="text-status-inactive">
          <Spinner /> Loading data streams...
        </Box>
      </Container>
    )
  }

  if (streams.length === 0) {
    return (
      <Container header={header}>
        <Box textAlign="center" padding="l" color="inherit">
          No data stream uploads found.
        </Box>
      </Container>
    )
  }

  return (
    <Container header={header}>
      <SpaceBetween size="m">
        {/* Legend */}
        <SpaceBetween direction="horizontal" size="l">
          <StatusIndicator type="success">Success</StatusIndicator>
          <StatusIndicator type="error">Failed</StatusIndicator>
          <StatusIndicator type="in-progress">Running</StatusIndicator>
        </SpaceBetween>

        {streams.map(({ stream, domain, runs }) => {
          const isExpanded = expandedStreams.has(stream.dataStreamId)
          const failedCount = runs.filter(
            (r) => r.status === RunStatus.Failed || r.status === RunStatus.PartiallyCompleted,
          ).length
          const latestRun = runs[0]
          const counter = `(${runs.length} upload${runs.length !== 1 ? "s" : ""}${
            failedCount > 0 ? `, ${failedCount} failed` : ""
          })`

          return (
            <ExpandableSection
              key={stream.dataStreamId}
              variant="container"
              headerText={domain}
              headerCounter={counter}
              headerActions={
                <StatusIndicator type={STATUS_INDICATOR[latestRun.status] ?? "pending"}>
                  Latest: {formatDate(latestRun.createdAt)}
                </StatusIndicator>
              }
              expanded={isExpanded}
              onChange={() => toggleStream(stream.dataStreamId)}
            >
              <div style={{ overflowX: "auto" }}>
                <div style={{ width: "max-content" }}>
                  <Steps
                    orientation="horizontal"
                    ariaLabel={`${domain} upload history`}
                    renderStep={(step) => {
                      const cfg = STEP_ICON[step.status] ?? STEP_ICON.pending
                      return {
                        header: step.header,
                        details: step.details,
                        icon: (
                          <Icon
                            name={cfg.name}
                            variant={cfg.variant}
                            size="big"
                            ariaLabel={step.statusIconAriaLabel}
                          />
                        ),
                      }
                    }}
                    steps={runs.map((run): StepsProps.Step => {
                      const failed =
                        run.status === RunStatus.Failed ||
                        run.status === RunStatus.PartiallyCompleted
                      return {
                        status: STATUS_INDICATOR[run.status] ?? "pending",
                        statusIconAriaLabel: run.status,
                        header: "",
                        details: (
                          <Box margin={{ vertical: "m" }}>
                            <Container disableContentPaddings>
                              <Box padding="s" textAlign="center">
                                <SpaceBetween size="xxs">
                                  {failed ? (
                                    <Link
                                      onFollow={() =>
                                        setSelectedRun({
                                          studyId,
                                          runId: run.runId,
                                          specId: run.specId,
                                        })
                                      }
                                    >
                                      {run.fileName || "Upload"}
                                    </Link>
                                  ) : (
                                    <Box fontWeight="bold">{run.fileName || "Upload"}</Box>
                                  )}
                                  <Box variant="strong" fontSize="body-s">
                                    {new Date(run.createdAt).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                    })}
                                  </Box>

                                  <Box fontSize="body-s" color="text-status-inactive">
                                    {new Date(run.createdAt).toLocaleTimeString("en-US", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </Box>
                                  {run.quarantineSummary?.totalFailed ? (
                                    <Box
                                      variant="strong"
                                      fontSize="body-s"
                                      color="text-status-error"
                                    >
                                      {run.quarantineSummary.totalFailed} quarantined
                                    </Box>
                                  ) : run.status === RunStatus.Completed ? (
                                    <StatusIndicator type="success">Loaded</StatusIndicator>
                                  ) : null}
                                  {run.dryRun && (
                                    <Box fontSize="body-s" color="text-status-info">
                                      Test Run
                                    </Box>
                                  )}
                                </SpaceBetween>
                              </Box>
                            </Container>
                          </Box>
                        ),
                      }
                    })}
                  />
                </div>
              </div>
            </ExpandableSection>
          )
        })}

        {selectedRun && (
          <QuarantineRecordsTable
            studyId={selectedRun.studyId}
            runId={selectedRun.runId}
            specId={selectedRun.specId}
            onClose={() => setSelectedRun(null)}
          />
        )}
      </SpaceBetween>
    </Container>
  )
}

export default DataStreamHistory
